import React, { useState, useEffect, useRef } from 'react';

interface AnalogClockProps {
  size: number;
  color?: string;
}

export function AnalogClock({ size, color = 'white' }: AnalogClockProps) {
  const [time, setTime] = useState(new Date());
  const rafRef = useRef<number>();

  useEffect(() => {
    let lastSecond = -1;
    const tick = () => {
      const now = new Date();
      if (now.getSeconds() !== lastSecond) {
        lastSecond = now.getSeconds();
        setTime(now);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const hours = time.getHours() % 12;
  const minutes = time.getMinutes();
  const seconds = time.getSeconds();

  const hourAngle = (hours + minutes / 60) * 30; // 360/12 = 30 deg per hour
  const minuteAngle = (minutes + seconds / 60) * 6; // 360/60 = 6 deg per minute
  const secondAngle = seconds * 6;

  const cx = 50;
  const cy = 50;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.4))' }}
    >
      {/* Clock face */}
      <circle cx={cx} cy={cy} r={48} fill="none" stroke={color} strokeWidth="1.5" opacity={0.4} />

      {/* Hour markers */}
      {Array.from({ length: 12 }, (_, i) => {
        const angle = (i * 30 - 90) * (Math.PI / 180);
        const isMain = i % 3 === 0;
        const r1 = isMain ? 40 : 42;
        const r2 = 46;
        return (
          <line
            key={i}
            x1={cx + r1 * Math.cos(angle)}
            y1={cy + r1 * Math.sin(angle)}
            x2={cx + r2 * Math.cos(angle)}
            y2={cy + r2 * Math.sin(angle)}
            stroke={color}
            strokeWidth={isMain ? 2.5 : 1}
            strokeLinecap="round"
            opacity={isMain ? 0.9 : 0.5}
          />
        );
      })}

      {/* Hour hand */}
      <line
        x1={cx}
        y1={cy}
        x2={cx + 28 * Math.cos((hourAngle - 90) * Math.PI / 180)}
        y2={cy + 28 * Math.sin((hourAngle - 90) * Math.PI / 180)}
        stroke={color}
        strokeWidth="3.5"
        strokeLinecap="round"
      />

      {/* Minute hand */}
      <line
        x1={cx}
        y1={cy}
        x2={cx + 38 * Math.cos((minuteAngle - 90) * Math.PI / 180)}
        y2={cy + 38 * Math.sin((minuteAngle - 90) * Math.PI / 180)}
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
      />

      {/* Second hand */}
      <line
        x1={cx}
        y1={cy}
        x2={cx + 42 * Math.cos((secondAngle - 90) * Math.PI / 180)}
        y2={cy + 42 * Math.sin((secondAngle - 90) * Math.PI / 180)}
        stroke="#DC2626"
        strokeWidth="1"
        strokeLinecap="round"
      />

      {/* Center dot */}
      <circle cx={cx} cy={cy} r="3" fill={color} />
      <circle cx={cx} cy={cy} r="1.5" fill="#DC2626" />
    </svg>
  );
}
